import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, TextField, InputAdornment } from '@mui/material';
import { AlternateEmail, Call } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { updateCompany } from '../../../redux/company/actions';
import { COMPANY_BRAND_ORANGE } from '../../../constants/companyBrand';

interface ICompanyContactEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const SURFACE_CARD = '#1a1a1a';
const INPUT_BLACK = '#000000';
const LABEL_MUTED = 'rgba(255,255,255,0.45)';

const CompanyContactEdit: React.FC<ICompanyContactEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        email: company?.email || '',
        phone_number: company?.phone_number || '',
    });
    const [initialData, setInitialData] = useState(formData);

    useEffect(() => {
        if (company) {
            const newData = {
                email: company.email || '',
                phone_number: company.phone_number || '',
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleFieldChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        if (company?._id) {
            dispatch(
                updateCompany({
                    id: company._id,
                    body: {
                        email: formData.email,
                        phone_number: formData.phone_number,
                    },
                }),
            );
        }
    };

    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: INPUT_BLACK,
            color: '#ffffff',
            borderRadius: 2,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            fontSize: '1rem',
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: `1px solid ${COMPANY_BRAND_ORANGE}` },
        },
        '& .MuiInputBase-input::placeholder': {
            color: 'rgba(255,255,255,0.35)',
            opacity: 1,
        },
    };

    const sectionTitle = (text: string) => (
        <Typography
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                fontFamily: FONT_ENUM.BOLD_HEADER,
                fontWeight: 700,
                fontSize: '1.125rem',
                color: '#ffffff',
                mb: 4,
            }}>
            <Box sx={{ width: 4, height: 24, bgcolor: COMPANY_BRAND_ORANGE, borderRadius: 1 }} />
            {text}
        </Typography>
    );

    const uppercaseLabel = (text: string) => (
        <Typography
            sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: LABEL_MUTED,
                mb: 1.5,
                fontFamily: FONT_ENUM.MEDIUM_TEXT,
            }}>
            {text}
        </Typography>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontWeight: 800,
                        fontSize: { xs: '1.75rem', md: '2.25rem' },
                        color: '#ffffff',
                        letterSpacing: '-0.02em',
                        mb: 1.5,
                    }}>
                    Contact{' '}
                    <Box component="span" sx={{ color: COMPANY_BRAND_ORANGE }}>
                        Information
                    </Box>
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.55)',
                        maxWidth: 560,
                        lineHeight: 1.65,
                        fontSize: '0.95rem',
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                    }}>
                    How talent and partners can reach your company.
                </Typography>
            </Box>

            <Box
                sx={{
                    bgcolor: SURFACE_CARD,
                    borderRadius: 3,
                    p: { xs: 2, sm: 4 },
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                {sectionTitle('Primary Communication')}
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        {uppercaseLabel('Email Address')}
                        <TextField
                            fullWidth
                            type="email"
                            value={formData.email}
                            onChange={e => handleFieldChange('email', e.target.value)}
                            disabled={!isEditing}
                            placeholder="Enter professional email"
                            sx={fieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AlternateEmail sx={{ color: 'rgba(255,255,255,0.4)', ml: 0.5 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {uppercaseLabel('Phone Number')}
                        <TextField
                            fullWidth
                            value={formData.phone_number}
                            onChange={e => handleFieldChange('phone_number', e.target.value)}
                            disabled={!isEditing}
                            placeholder="+1 (000) 000-0000"
                            sx={fieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Call sx={{ color: 'rgba(255,255,255,0.4)', ml: 0.5 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export { CompanyContactEdit };
